import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, AlertTriangle, Zap, FileText, Home, Plug, ArrowLeft, CheckCircle2, UserPlus, Beaker, History, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const SS_KEY = "rete_session";

const TOOLS = {
  consulenza: { label: "Consulenza completa", desc: "Analisi bolletta + tetto + preventivo + PDF", Icon: FileText },
  bolletta: { label: "Analisi bolletta", desc: "Stima risparmio dalla bolletta", Icon: Zap },
  tetto: { label: "Analisi tetto", desc: "Fattibilità e layout pannelli", Icon: Home },
  colonnina: { label: "Colonnina EV", desc: "Dimensionamento wallbox e risparmio", Icon: Plug },
};

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(SS_KEY)}` } });
const isRecent = (iso, days = 30) => { if (!iso) return false; const d = new Date(iso); return !isNaN(d) && (Date.now() - d.getTime()) <= days * 86400000; };

function TorriBadge({ torri, small }) {
  return (
    <div className="flex items-center gap-2" data-testid="rete-torri-logo">
      <img src={torri.logo} alt="TORRI.SOLAR" className={small ? "h-[29px] w-[29px] rounded-full object-contain" : "h-9 w-9 rounded-full object-contain"} />
      <div className="leading-tight">
        <div className={`font-heading font-extrabold ${small ? "text-[17px]" : "text-sm"} tracking-wide`}>TORRI<span style={{ color: "#FDD003" }}>.SOLAR</span></div>
        <div className={`${small ? "text-[14px]" : "text-[10px]"} text-muted-foreground uppercase tracking-[0.15em]`}>{torri.sub}</div>
      </div>
    </div>
  );
}

function CoBrandHeader({ ctx }) {
  const { brand_mode, torri, installer, partner_note } = ctx;
  if (installer?.ragione_sociale) {
    const isPartner = brand_mode === "partner";
    return (
      <div className="border-b border-border bg-background/95 backdrop-blur px-4 sm:px-6 py-3" data-testid="rete-header">
        <div className="mx-auto max-w-5xl flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          {isPartner ? (
            <div className="flex items-center gap-3 min-w-0">
              {installer.logo_url ? (
                <img src={installer.logo_url} alt={installer.ragione_sociale} className="h-11 w-11 rounded-lg object-contain bg-white border border-border shrink-0" data-testid="rete-partner-logo" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <div className="h-11 w-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-heading font-extrabold text-lg shrink-0" data-testid="rete-partner-logo">{(installer.ragione_sociale || "?").charAt(0).toUpperCase()}</div>
              )}
              <div className="leading-tight min-w-0">
                <div className="font-heading font-extrabold text-base truncate" data-testid="rete-installer-name">{installer.ragione_sociale}</div>
                <div className="text-[11px] text-muted-foreground truncate">{[installer.citta, installer.provincia].filter(Boolean).join(" · ")}</div>
                {partner_note && <span className="inline-block mt-1 text-[9px] font-semibold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-2 py-0.5 whitespace-nowrap" data-testid="rete-partner-note">{partner_note}</span>}
              </div>
            </div>
          ) : (
            <TorriBadge torri={torri} />
          )}
          <div className="flex flex-col items-end gap-0.5 shrink-0 sm:justify-end">
            {isPartner ? (
              <TorriBadge torri={torri} small />
            ) : (
              <>
                <span className="text-[10px] text-muted-foreground text-right whitespace-nowrap">In collaborazione con l'installatore</span>
                <div className="leading-tight text-right min-w-0" data-testid="rete-installer-anagrafica">
                  <div className="font-heading font-extrabold text-sm truncate" data-testid="rete-installer-name">{installer.ragione_sociale}</div>
                  {installer.indirizzo && <div className="text-[11px] text-muted-foreground">{installer.indirizzo}</div>}
                  <div className="text-[11px] text-muted-foreground">{[installer.citta, installer.provincia].filter(Boolean).join(" · ")}</div>
                  {installer.p_iva && <div className="text-[11px] text-muted-foreground">P.IVA {installer.p_iva}</div>}
                  {installer.telefono && <div className="text-[11px] text-muted-foreground">Tel. {installer.telefono}</div>}
                  {installer.email && <div className="text-[11px] text-muted-foreground break-all">{installer.email}</div>}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="border-b border-border bg-background/95 backdrop-blur px-4 sm:px-6 py-3" data-testid="rete-header">
      <div className="mx-auto max-w-5xl flex items-center justify-between gap-4"><TorriBadge torri={torri} /></div>
    </div>
  );
}

function FeaturingBlock({ installer, installerType }) {
  const label = installerType === "prospect" ? "Installatore segnalato (prospect)" : "In collaborazione con l'installatore";
  return (
    <div className="rounded-xl border border-border bg-muted/40 p-4 flex items-center gap-3" data-testid="rete-featuring-installer">
      <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-heading font-bold">{(installer.ragione_sociale || "?").charAt(0).toUpperCase()}</div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
        <div className="font-heading font-bold text-sm truncate" data-testid="rete-installer-name">{installer.ragione_sociale}</div>
        <div className="text-[11px] text-muted-foreground">{[installer.citta, installer.provincia].filter(Boolean).join(" · ")}</div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary/60";

function ReusablePreview({ it }) {
  const p = it.preview || {};
  if (p.type === "consumption") {
    const vals = (p.monthly && p.monthly.length) ? p.monthly : (p.fasce ? [p.fasce.f1 || 0, p.fasce.f2 || 0, p.fasce.f3 || 0] : []);
    if (!vals.length) return null;
    const max = Math.max(...vals, 1);
    const isMonthly = !!(p.monthly && p.monthly.length);
    return (
      <div className="mt-1.5" data-testid="rete-preview-consumption">
        <div className="flex items-end gap-[2px] h-8">
          {vals.map((v, i) => (
            <div key={i} className="flex-1 min-w-[3px] rounded-t-sm bg-primary/60" style={{ height: `${Math.max(10, (v / max) * 100)}%` }} title={`${Math.round(v)} kWh`} />
          ))}
        </div>
        <div className="text-[9px] text-muted-foreground mt-0.5">{isMonthly ? "Consumo mensile (kWh)" : "Consumo per fascia F1·F2·F3 (kWh)"}</div>
      </div>
    );
  }
  if (p.type === "layout") {
    const rows = Array.isArray(p.rows) ? p.rows.filter((n) => n > 0) : [];
    if (!rows.length) return null;
    const tot = rows.reduce((a, b) => a + b, 0);
    return (
      <div className="mt-1.5" data-testid="rete-preview-layout">
        <div className="inline-flex flex-col gap-[2px] rounded bg-background/70 border border-border p-1">
          {rows.map((n, ri) => (
            <div key={ri} className="flex gap-[2px]">
              {Array.from({ length: Math.min(n, 24) }).map((_, ci) => (
                <div key={ci} className="h-2 w-2 rounded-[1px] bg-primary/70" />
              ))}
            </div>
          ))}
        </div>
        <div className="text-[9px] text-muted-foreground mt-0.5">Schema moduli · {tot} pannelli su {rows.length} file</div>
      </div>
    );
  }
  if (p.type === "estimate") {
    const chips = [];
    if (p.pv_kwp) chips.push(`${p.pv_kwp} kWp`);
    if (p.storage_kwh) chips.push(`${p.storage_kwh} kWh accumulo`);
    if (!chips.length) return null;
    return (
      <div className="mt-1.5 flex flex-wrap gap-1" data-testid="rete-preview-estimate">
        {chips.map((c, i) => <span key={i} className="rounded bg-primary/10 text-primary text-[9px] font-semibold px-1.5 py-0.5">{c}</span>)}
      </div>
    );
  }
  return null;
}

function ConsumerStep({ tool, onBack, onDone }) {
  const [mode, setMode] = useState("nuovo");
  const [form, setForm] = useState({ entity_type: "persona_fisica", nome: "", cognome: "", ragione_sociale: "", partita_iva: "", codice_fiscale: "", email: "", phone: "", address: "", citta: "", provincia: "", cap: "" });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const [existing, setExisting] = useState([]);
  const [dupConfirmed, setDupConfirmed] = useState(false);
  const [needConfirm, setNeedConfirm] = useState(false);
  const [selExisting, setSelExisting] = useState(null);
  const [reusable, setReusable] = useState([]);
  const [reusableLoading, setReusableLoading] = useState(false);
  const [imports, setImports] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Controllo "già processato": cerca consulenze esistenti in qualsiasi canale (Free/Rete/Lead)
  useEffect(() => {
    if (mode !== "nuovo") { setExisting([]); return; }
    setDupConfirmed(false); setNeedConfirm(false); setSelExisting(null);
    const email = form.email.trim();
    const phone = form.phone.trim();
    const nm = form.entity_type === "azienda" ? form.ragione_sociale.trim() : `${form.nome} ${form.cognome}`.trim();
    const piva = form.partita_iva.trim();
    const enough = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || phone.replace(/\D/g, "").length >= 6 || piva.replace(/\D/g, "").length >= 8 || nm.length >= 4;
    if (!enough) { setExisting([]); return; }
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/rete/consumers/check`, { params: { email, phone, name: nm, ragione_sociale: form.ragione_sociale, partita_iva: piva }, ...auth() });
        setExisting(data.matches || []);
      } catch { setExisting([]); }
    }, 500);
    return () => clearTimeout(timer);
  }, [mode, form.email, form.phone, form.nome, form.cognome, form.ragione_sociale, form.partita_iva, form.entity_type]);

  // Anagrafica selezionata: cerca tutto ciò che è riutilizzabile a suo nome (analisi/layout/preventivo)
  useEffect(() => {
    if (!selExisting?.id) { setReusable([]); setImports({}); return; }
    setImports({}); setReusableLoading(true);
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/rete/consumers/reusable`, { params: { lead_id: selExisting.id, name: selExisting.name }, ...auth() });
        if (alive) setReusable(data.items || []);
      } catch { if (alive) setReusable([]); }
      finally { if (alive) setReusableLoading(false); }
    })();
    return () => { alive = false; };
  }, [selExisting]);

  const clickNuovo = () => {
    if (existing.some((m) => isRecent(m.date)) && !dupConfirmed) { setNeedConfirm(true); return; }
    attach({ mode: "nuovo", consumer: form });
  };

  const toggleImport = (it) => setImports((prev) => {
    if (prev[it.kind] === it.source_lead_id) { const n = { ...prev }; delete n[it.kind]; return n; }
    return { ...prev, [it.kind]: it.source_lead_id };
  });

  const loadExisting = (m) => {
    if (!m?.id) { setErr("Impossibile caricare questo nominativo (riferimento mancante)."); return; }
    attach({ mode: "esistente", consumer_lead_id: m.id, import_items: imports });
  };

  const attach = async (payload) => {
    setErr("");
    if (payload.mode === "nuovo") {
      const f = payload.consumer || {};
      const tr = (v) => (v || "").trim();
      if (f.entity_type === "azienda") {
        if (!tr(f.ragione_sociale)) return setErr("La ragione sociale è obbligatoria");
      } else {
        if (!tr(f.nome) || !tr(f.cognome)) return setErr("Nome e cognome sono obbligatori");
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tr(f.email))) return setErr("Inserisci un'email valida");
      if (!tr(f.phone)) return setErr("Il telefono è obbligatorio");
      if (!tr(f.address)) return setErr("L'indirizzo è obbligatorio");
      if (!tr(f.citta)) return setErr("La città è obbligatoria");
      if (!tr(f.provincia)) return setErr("La provincia è obbligatoria");
      if (!tr(f.cap)) return setErr("Il CAP è obbligatorio");
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/rete/attach-consumer`, { tool, ...payload }, auth());
      if (data.engine_ready && data.continue_url) { window.location.href = data.continue_url; return; }
      onDone(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Errore durante l'avvio della pratica");
      setBusy(false);
    }
  };

  const t = TOOLS[tool];
  return (
    <div className="space-y-5" data-testid="rete-consumer-step">
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="rete-back-btn"><ArrowLeft className="h-4 w-4" /> Strumenti</button>
      <div>
        <h1 className="font-heading font-extrabold text-2xl">{t?.label}</h1>
        <p className="text-sm text-muted-foreground">Indica il cliente finale (consumer) per cui stai configurando.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[["nuovo", "Nuovo cliente", UserPlus], ["demo", "Consulenza demo", Beaker]].map(([m, l, Icon]) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`rete-mode-${m}`}
            className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${mode === m ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}>
            <Icon className="h-4 w-4" /> {l}
          </button>
        ))}
      </div>

      {err && <div className="text-sm text-destructive" data-testid="rete-consumer-error">{err}</div>}

      {mode === "nuovo" && (
        <div className="space-y-3" data-testid="rete-form-nuovo">
          <div className="flex gap-2">
            {[["persona_fisica", "Persona fisica"], ["azienda", "Azienda"]].map(([v, l]) => (
              <button key={v} onClick={() => set("entity_type", v)} className={`flex-1 rounded-lg border px-3 py-2 text-sm ${form.entity_type === v ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`} data-testid={`rete-entity-${v}`}>{l}</button>
            ))}
          </div>
          {form.entity_type === "azienda" ? (
            <div className="grid sm:grid-cols-2 gap-2">
              <input className={inputCls} placeholder="Ragione sociale *" value={form.ragione_sociale} onChange={(e) => set("ragione_sociale", e.target.value)} data-testid="rete-in-ragione" />
              <input className={inputCls} placeholder="Partita IVA" value={form.partita_iva} onChange={(e) => set("partita_iva", e.target.value)} data-testid="rete-in-piva" />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              <input className={inputCls} placeholder="Nome *" value={form.nome} onChange={(e) => set("nome", e.target.value)} data-testid="rete-in-nome" />
              <input className={inputCls} placeholder="Cognome *" value={form.cognome} onChange={(e) => set("cognome", e.target.value)} data-testid="rete-in-cognome" />
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-2">
            <input className={inputCls} placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="rete-in-email" />
            <input className={inputCls} placeholder="Telefono *" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="rete-in-phone" />
          </div>
          <input className={inputCls} placeholder="Indirizzo *" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="rete-in-address" />
          <div className="grid grid-cols-3 gap-2">
            <input className={inputCls} placeholder="Città *" value={form.citta} onChange={(e) => set("citta", e.target.value)} data-testid="rete-in-citta" />
            <input className={inputCls} placeholder="Provincia *" value={form.provincia} onChange={(e) => set("provincia", e.target.value)} data-testid="rete-in-provincia" />
            <input className={inputCls} placeholder="CAP *" value={form.cap} onChange={(e) => set("cap", e.target.value)} data-testid="rete-in-cap" />
          </div>
          <button disabled={busy} onClick={clickNuovo} className="w-full rounded-lg bg-primary text-primary-foreground font-semibold py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="rete-submit-nuovo">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Avvia configurazione
          </button>
          {needConfirm && (
            <div className="rounded-lg border-2 border-red-500/60 bg-red-500/10 p-3 space-y-2" data-testid="rete-dup-confirm">
              <div className="flex items-center gap-2 text-red-600 font-semibold text-sm"><AlertTriangle className="h-4 w-4" /> Consulenza recente già presente</div>
              <p className="text-xs text-muted-foreground">Per questo cliente risulta una consulenza creata negli ultimi 30 giorni. Vuoi comunque crearne una nuova?</p>
              <div className="flex gap-2">
                <button onClick={() => { setDupConfirmed(true); setNeedConfirm(false); attach({ mode: "nuovo", consumer: form }); }} disabled={busy} className="rounded-lg bg-red-600 text-white font-semibold px-3 py-2 text-sm disabled:opacity-60" data-testid="rete-dup-confirm-proceed">Crea comunque</button>
                <button onClick={() => setNeedConfirm(false)} className="rounded-lg border border-border px-3 py-2 text-sm" data-testid="rete-dup-confirm-cancel">Annulla</button>
              </div>
            </div>
          )}
          {existing.length > 0 && (
            <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 space-y-2" data-testid="rete-existing-panel">
              <div className="flex items-center gap-2 text-amber-600 font-semibold text-sm">
                <History className="h-4 w-4" /> Anagrafiche già presenti per questo cliente ({existing.length})
              </div>
              <p className="text-[11px] text-muted-foreground">Questo nominativo risulta già lavorato nel configuratore (qualsiasi canale). Le voci in rosso sono recenti (ultimi 30 giorni). <b>Clicca un'anagrafica per selezionarla e caricarla</b>.</p>
              <div className="space-y-1.5">
                {existing.map((m, i) => {
                  const sel = !!(selExisting && selExisting.id && m.id && selExisting.id === m.id);
                  return (
                  <button type="button" key={m.id || i} onClick={() => setSelExisting(m)} disabled={busy || !m.id}
                    className={`w-full text-left rounded border p-2 text-xs transition-colors disabled:opacity-60 ${sel ? "border-primary bg-primary/10 ring-1 ring-primary/40" : isRecent(m.date) ? "border-red-500/60 bg-red-500/5 hover:border-primary/50" : "border-border bg-background/60 hover:border-primary/50"}`} data-testid={`rete-existing-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center rounded-full bg-primary/15 text-primary text-[10px] font-bold px-1.5 py-0.5">{m.channel}</span>
                      <span className="font-medium">{m.name}</span>
                      {m.stage ? <span className="text-muted-foreground">· {m.stage}</span> : null}
                      {isRecent(m.date) ? <span className="text-red-600 font-semibold">· recente</span> : null}
                      {sel ? <CheckCircle2 className="h-3.5 w-3.5 text-primary ml-auto" /> : null}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {[m.city, m.date ? new Date(m.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : null, m.managed_by ? `Gestito da ${m.managed_by}` : null].filter(Boolean).join(" · ")}
                    </div>
                  </button>
                );})}
              </div>
              {selExisting && (
                <div className="space-y-2">
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-2.5 space-y-2" data-testid="rete-reusable-panel">
                    <div className="text-[11px] font-semibold text-foreground">Elementi caricabili per «{selExisting.name}»</div>
                    {reusableLoading ? (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Ricerca in corso…</div>
                    ) : reusable.length === 0 ? (
                      <div className="text-[11px] text-muted-foreground">Nessun elemento riutilizzabile trovato. Verrà caricata solo l'anagrafica.</div>
                    ) : (
                      <>
                        <div className="space-y-1.5">
                          {reusable.map((it, i) => {
                            const on = imports[it.kind] === it.source_lead_id;
                            return (
                              <button type="button" key={`${it.kind}-${it.source_lead_id}-${i}`} onClick={() => toggleImport(it)} disabled={busy}
                                className={`w-full text-left rounded border p-2 flex items-start gap-2 transition-colors disabled:opacity-60 ${on ? "border-primary bg-primary/10" : "border-border bg-background/60 hover:border-primary/50"}`} data-testid={`rete-reusable-${it.kind}-${i}`}>
                                <span className={`mt-0.5 h-4 w-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"}`}>{on ? <Check className="h-3 w-3" /> : null}</span>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-xs">{it.label}</span>
                                    <span className="inline-flex items-center rounded-full bg-primary/15 text-primary text-[9px] font-bold px-1.5 py-0.5">{it.channel}</span>
                                  </div>
                                  <div className="text-[11px] text-muted-foreground">{[it.summary, it.date ? new Date(it.date).toLocaleDateString("it-IT") : null].filter(Boolean).join(" · ")}</div>
                                  <ReusablePreview it={it} />
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-muted-foreground">Conferma cosa riprendere (una voce per tipo). Lascia tutto spento per partire solo dall'anagrafica.</p>
                      </>
                    )}
                  </div>
                  <button onClick={() => loadExisting(selExisting)} disabled={busy || reusableLoading} className="w-full rounded-lg bg-primary text-primary-foreground font-semibold px-3 py-2 text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="rete-existing-load">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Carica «{selExisting.name}» e procedi
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {mode === "demo" && (
        <div className="space-y-3" data-testid="rete-form-demo">
          <p className="text-sm text-muted-foreground">Crea una pratica dimostrativa senza profilare un cliente finale (numerazione progressiva).</p>
          <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 flex gap-2.5 text-sm" data-testid="rete-demo-disclaimer">
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-amber-500">Attenzione: usa la consulenza demo solo per prove e dimostrazioni.</div>
              <div className="text-muted-foreground mt-1">La pratica demo non è collegata a un cliente finale: non possiamo tracciare il cliente, attribuirti la pratica né seguirne l'avanzamento (proposta, contratto, caparra, provvigioni). L'invio al cliente è disattivato. Se stai lavorando con un cliente reale, usa <b className="text-foreground">"Nuovo cliente"</b>.</div>
            </div>
          </div>
          <button disabled={busy} onClick={() => { if (window.confirm("Consulenza demo: non potremo tracciare il cliente finale e l'invio al cliente sarà disattivato. Ho capito, è solo una prova.")) attach({ mode: "demo" }); }} className="w-full rounded-lg bg-primary text-primary-foreground font-semibold py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="rete-submit-demo">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Beaker className="h-4 w-4" />} Crea consulenza demo
          </button>
        </div>
      )}
    </div>
  );
}

function DonePanel({ result, onNew }) {
  return (
    <div className="rounded-xl border border-green-500/40 bg-green-500/10 p-6 text-center space-y-3" data-testid="rete-done">
      <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
      <h2 className="font-heading font-bold text-lg">Pratica creata</h2>
      <p className="text-sm">Cliente: <b>{result.consumer_name}</b> · Strumento: <b>{TOOLS[result.tool]?.label || result.tool}</b></p>
      <p className="text-xs text-muted-foreground">Il collegamento al motore per questo strumento è in arrivo. La pratica è salvata ed è già visibile su rete.torri.solar.</p>
      <button onClick={onNew} className="rounded-lg border border-border px-4 py-2 text-sm font-semibold" data-testid="rete-new-btn">Nuova configurazione</button>
    </div>
  );
}

const STATUS_IT = { bozza: "Bozza", in_lavorazione: "In lavorazione", completata: "Completata" };

function PraticheList({ items, onResume, onNew, busy }) {
  return (
    <div className="space-y-5" data-testid="rete-pratiche-list">
      <div>
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl">Le tue pratiche</h1>
        <p className="text-sm text-muted-foreground">Riprendi una configurazione già avviata con questo installatore oppure iniziane una nuova.</p>
      </div>
      <button onClick={onNew} className="w-full sm:w-auto rounded-xl border border-primary/50 bg-primary/10 hover:bg-primary/15 px-4 py-3 flex items-center gap-2 font-heading font-bold text-sm transition-colors" data-testid="rete-pratiche-new">
        <UserPlus className="h-4 w-4 text-primary" /> Nuova configurazione
      </button>
      <div className="rounded-xl border border-border bg-card divide-y divide-border">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-3 p-3 sm:p-4" data-testid={`rete-pratica-row-${it.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{it.consumer_name}{it.city ? ` · ${it.city}` : ""}</div>
              <div className="text-xs text-muted-foreground truncate">{it.tool_label || it.tool} · {STATUS_IT[it.status] || it.status} · {it.updated_at ? new Date(it.updated_at).toLocaleDateString("it-IT") : ""}</div>
            </div>
            <button onClick={() => onResume(it.id)} disabled={!!busy} className="rounded-lg border border-border hover:border-primary/50 px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 transition-colors" data-testid={`rete-pratica-resume-${it.id}`}>
              {busy === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <History className="h-3.5 w-3.5 text-primary" />} Riprendi
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PraticaPanel({ p }) {
  return (
    <div className="space-y-5" data-testid="rete-pratica-panel">
      <div>
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl">Pratica in corso</h1>
        <p className="text-sm text-muted-foreground">Cliente: <b data-testid="rete-pratica-cliente">{p.consumer_name || "—"}</b> · Strumento: <b>{p.tool_label || p.tool}</b></p>
      </div>
      {p.unlocked ? (
        <div className="rounded-xl border border-border bg-card p-4 text-sm" data-testid="rete-pratica-unlocked">Pratica presa in carico da Torri.Solar: la proposta è gestita internamente.</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {p.consulenza_url && (
            <a href={p.consulenza_url} data-testid="rete-pratica-open-consulenza" className="rounded-xl border border-primary/50 bg-primary/10 hover:bg-primary/15 p-4 flex items-start gap-3 transition-colors">
              <FileText className="h-5 w-5 text-primary mt-0.5" />
              <div><div className="font-heading font-bold text-sm">Apri consulenza, prezzi e proposta</div><div className="text-xs text-muted-foreground">Scheda di costo, prezzo finale, Outlook per il cliente</div></div>
            </a>
          )}
          {p.continue_url && (
            <a href={p.continue_url} data-testid="rete-pratica-continue" className="rounded-xl border border-border hover:border-primary/50 bg-card p-4 flex items-start gap-3 transition-colors">
              <History className="h-5 w-5 text-primary mt-0.5" />
              <div><div className="font-heading font-bold text-sm">Modifica configurazione</div><div className="text-xs text-muted-foreground">Bolletta, tetto e layout pannelli</div></div>
            </a>
          )}
          {p.outlook_url && (
            <a href={p.outlook_url} target="_blank" rel="noopener noreferrer" data-testid="rete-pratica-outlook" className="rounded-xl border border-border hover:border-primary/50 bg-card p-4 flex items-start gap-3 transition-colors">
              <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
              <div><div className="font-heading font-bold text-sm">Vedi proposta inviata</div><div className="text-xs text-muted-foreground">Come la vede il cliente</div></div>
            </a>
          )}
        </div>
      )}
      <p className="text-xs text-muted-foreground" data-testid="rete-pratica-new-hint">Per un nuovo cliente avvia una nuova configurazione da rete.torri.solar.</p>
    </div>
  );
}

export default function RetePage() {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState("tools"); // tools | consumer
  const [activeTool, setActiveTool] = useState(null);
  const [done, setDone] = useState(null);
  const showPratica = true;
  const [newMode, setNewMode] = useState(false);
  const [resuming, setResuming] = useState(null);
  const [wantToolState, setWantToolState] = useState(null);
  const resume = async (id) => {
    setResuming(id);
    try {
      const { data } = await axios.post(`${API}/api/rete/resume/${id}`, {}, auth());
      localStorage.setItem(SS_KEY, data.session_token);
      const pr = data.context?.pratica;
      if (pr?.tool === "tetto" && pr.continue_url) { window.location.href = pr.continue_url; return; }
      setCtx(data.context);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "Impossibile riprendere la pratica.");
    } finally { setResuming(null); }
  };
  const startNew = () => {
    setNewMode(true);
    if (wantToolState && TOOLS[wantToolState] && (ctx.tools || []).includes(wantToolState)) { setActiveTool(wantToolState); setView("consumer"); }
  };

  useEffect(() => {
    document.title = "Configuratore Rete · Torri.Solar";
    const params = new URLSearchParams(window.location.search);
    const t = params.get("t");
    const wantTool = params.get("tool");
    setWantToolState(wantTool);
    const openTool = (c) => {
      if (c.pratica || (c.pratiche || []).length) return;
      if (wantTool && TOOLS[wantTool] && (c.tools || []).includes(wantTool)) {
        setActiveTool(wantTool); setView("consumer");
      }
    };
    const run = async () => {
      try {
        if (t) {
          const { data } = await axios.post(`${API}/api/rete/launch`, { t, config_id: params.get("config_id") || params.get("configId") || undefined });
          localStorage.setItem(SS_KEY, data.session_token);
          setCtx(data.context);
          openTool(data.context);
          window.history.replaceState({}, "", "/rete");
        } else {
          const sess = localStorage.getItem(SS_KEY);
          if (!sess) { setError("Accesso non valido. Apri il configuratore da rete.torri.solar."); return; }
          const { data } = await axios.get(`${API}/api/rete/context`, auth());
          setCtx(data);
          openTool(data);
        }
      } catch (e) {
        const d = e.response?.data?.detail;
        const st = e.response?.status;
        const step = t ? "launch" : "context";
        if (typeof d === "string") setError(`${d} (${step} · HTTP ${st})`);
        else if (Array.isArray(d)) setError(`Richiesta non valida: ${d.map((x) => `${(x.loc || []).join(".")} ${x.msg}`).join("; ")} (${step} · HTTP ${st})`);
        else if (st) setError(`Errore del server (${step} · HTTP ${st}). Riprova tra poco o riapri il configuratore da rete.torri.solar.`);
        else setError(`Server non raggiungibile (${step} · ${e.message || "errore di rete"}). Riprova tra poco.`);
      } finally { setLoading(false); }
    };
    run();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="rete-error">
      <div className="max-w-md text-center">
        <AlertTriangle className="h-10 w-10 text-destructive mx-auto mb-3" />
        <h1 className="font-heading font-bold text-lg mb-1">Accesso non disponibile</h1>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    </div>
  );

  const tools = ctx.tools || [];
  return (
    <div className="min-h-screen bg-background" data-testid="rete-page">
      <CoBrandHeader ctx={ctx} />
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {ctx.pratica && showPratica && !done && view === "tools" && <PraticaPanel p={ctx.pratica} />}
        {!ctx.pratica && (ctx.pratiche || []).length > 0 && !newMode && !done && view === "tools" && <PraticheList items={ctx.pratiche} onResume={resume} onNew={startNew} busy={resuming} />}
        {view === "tools" && !done && !(ctx.pratica && showPratica) && !((ctx.pratiche || []).length > 0 && !newMode) && (
          <>
            <div>
              <h1 className="font-heading font-extrabold text-2xl sm:text-3xl">Nuova configurazione</h1>
              <p className="text-sm text-muted-foreground">Agente: <b data-testid="rete-agent-name">{ctx.agent?.name || "—"}</b>{ctx.agent?.agency_name ? ` · ${ctx.agent.agency_name}` : ""}</p>
            </div>
            {ctx.brand_mode !== "partner" && ctx.installer?.ragione_sociale && <FeaturingBlock installer={ctx.installer} installerType={ctx.installer_type} />}
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Scegli lo strumento</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {tools.length === 0 && <div className="text-sm text-muted-foreground" data-testid="rete-no-tools">Nessuno strumento abilitato per questo agente.</div>}
                {tools.map((k) => {
                  const tt = TOOLS[k]; if (!tt) return null; const { Icon } = tt;
                  return (
                    <button key={k} data-testid={`rete-tool-${k}`}
                      className="text-left rounded-xl border border-border hover:border-primary/50 bg-card p-4 transition-colors flex items-start gap-3"
                      onClick={() => { setActiveTool(k); setView("consumer"); }}>
                      <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0"><Icon className="h-5 w-5" /></div>
                      <div><div className="font-heading font-bold text-sm">{tt.label}</div><div className="text-xs text-muted-foreground">{tt.desc}</div></div>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {view === "consumer" && !done && (
          <ConsumerStep tool={activeTool} onBack={() => setView("tools")} onDone={(res) => { setDone(res); setView("tools"); }} />
        )}

        {done && <DonePanel result={done} onNew={() => setDone(null)} />}
      </div>
    </div>
  );
}
